<template>
  <MMPopupDefault class="feedback-modal" @close="$emit('close')">
    <template #header>
      <div class="popup-header">
        <MMText font="Title L/Semibold" class="header-title"> Отзывы </MMText>
        <button @click="openSort" class="sort-button" aria-label="Настройки сортировки">
          <IconSort :size="28" color="tertiary" />
        </button>
      </div>
    </template>

    <div class="modal-content">
      <div v-if="feedbackList.length > 0" class="feedback-list">
        <div v-for="feedback in feedbackList" :key="feedback.id" class="feedback-item">
          <ProductTabsReviewsItem
            :feedback="feedback"
            @event-show-review="eventShow(feedback)"
            :itsFeedbackMobile="true"
          />
        </div>

        <button
          v-if="hasMoreFeedback && !feedbackLoading"
          @click="loadMoreFeedback"
          class="load-more-button"
          :disabled="feedbackLoading"
        >
          Показать ещё
        </button>

        <div v-if="feedbackLoading" class="loading-state">
          <MMText font="Body M/Regular" color="secondary"> Загрузка отзывов... </MMText>
        </div>
      </div>

      <div v-else-if="!feedbackLoading" class="empty-state">
        <MMText font="Body M/Regular" color="secondary"> Отзывы не найдены </MMText>
      </div>
    </div>
  </MMPopupDefault>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { MMPopupDefault, MMText } from '@mm-frontend/mm-web-ui-kit';
import { IconSort } from '@/features/ui-kit/icons';
import ProductTabsReviewsItem from '../common/tabs-reviews/product-tabs-reviews-item.vue';
import { useFeedback } from '../../business/useFeedback';
import { useProductModals } from '../../business/useProductModals';
import type { Feedback } from '@/shared/apollo-api/__generated__/types';
import { FeedbackSortType, FeedbackFilterType } from '@/shared/apollo-api/__generated__/types';

interface IPopupData {
  productId: number;
  feedbackQuantity: number;
  rating: number;
}

const props = defineProps<{ onShowData: IPopupData }>();

defineEmits<{
  close: [];
}>();

// Используем useFeedback composable для получения реальных данных
const {
  feedback: feedbackList,
  feedbackLoading,
  hasMoreFeedback,
  loadMoreFeedback: loadMoreFeedbackFromComposable,
  loadFeedback,
  initializeFeedback,
  currentSortValue,
} = useFeedback();

const { openSortFeedbackModal, closeSortFeedbackModal, setSortApplyHandler } = useProductModals();

// Обработчик применения сортировки
const handleSortApply = (sort: FeedbackSortType, filters: FeedbackFilterType[]) => {
  // Применяем сортировку через useFeedback
  loadFeedback(0, 10, filters, sort);
  closeSortFeedbackModal();
};

// Устанавливаем обработчик при монтировании компонента
onMounted(() => {
  setSortApplyHandler(handleSortApply);
  initializeFeedback();
});

const openSort = () => {
  // Получаем текущие фильтры из useFeedback
  const currentFilters: FeedbackFilterType[] = []; // TODO: Получить из useFeedback
  openSortFeedbackModal(currentSortValue.value, currentFilters);
};

const loadMoreFeedback = () => {
  loadMoreFeedbackFromComposable();
};

const eventShow = (feedback: Feedback) => {
  // TODO: Интегрировать с аналитикой
  console.log('Analytics: FEEDBACK_IMPRESSION', {
    space_name: 'ProductReviews',
    space_type: 'PAGE',
    space_id: String(props.onShowData.productId),
    action: 'SHOW',
    product_id: props.onShowData.productId,
    feedback_id: feedback.id,
    rating: feedback.rating,
    is_edited: false,
    is_anonymous: feedback.anonymous,
    has_text: Boolean(feedback.content),
    has_seller_response: Boolean(feedback.reply),
    has_media: Boolean(feedback.photos),
    page_type: 'PRODUCT_REVIEWS',
  });
};
</script>

<style scoped lang="stylus">
.feedback-modal
  width 100%
  max-width 480px
  max-height 80vh

  +Media(Mobile)
    max-width calc(100vw - 32px)
    max-height calc(100vh - 32px)

.popup-header
  display flex
  align-items center
  justify-content space-between
  position relative

.header-title
  text-align center
  flex 1

.sort-button
  position absolute
  right 0
  top 0
  width 56px
  height 56px
  display flex
  align-items center
  justify-content center
  background none
  border none
  cursor pointer
  border-radius 8px
  transition background-color 0.2s ease

  &:hover
    background-color rgba(0, 0, 0, 0.05)

  &:active
    background-color rgba(0, 0, 0, 0.1)

.modal-content
  padding 12px 16px 12px
  max-height 60vh
  overflow-y auto

  +Media(Mobile)
    max-height calc(100vh - 120px)

.feedback-list
  display flex
  flex-direction column
  gap 0

.feedback-item
  padding 15px 0
  border-bottom 1px solid var(--color-border-secondary)

  &:last-child
    border-bottom none

.load-more-button
  width 100%
  padding 16px 24px
  margin-top 24px
  border-radius 12px
  background-color var(--color-background-secondary)
  border none
  cursor pointer
  color var(--text-primary)
  font-family Inter
  font-size 16px
  font-weight 500
  line-height 20px
  transition background-color 0.2s ease

  &:hover:not(:disabled)
    background-color var(--color-background-tertiary)

  &:disabled
    opacity 0.6
    cursor not-allowed

.loading-state
  text-align center
  padding 20px

.empty-state
  text-align center
  padding 20px
</style>
